import { useRef } from "react";
import { Shuffle } from "lucide-react";

const STEP_DEG = 24; // degrees of drag per list step

// Original on-screen scroll wheel (a navigation control, not a device picture).
// Drag/scroll around the ring to step; center selects; MENU goes back.
export default function Wheel({ onStep, onSelect, onSelectHold, onMenu, onShuffle, size = 196 }) {
  const ref = useRef(null);
  const drag = useRef(null);
  const hold = useRef({ t: null, fired: false });
  const holdDown = () => {
    if (!onSelectHold) return;
    hold.current.fired = false;
    clearTimeout(hold.current.t);
    hold.current.t = setTimeout(() => { hold.current.fired = true; onSelectHold(); }, 500);
  };
  const holdEnd = () => clearTimeout(hold.current.t);
  const selectClick = () => { if (hold.current.fired) { hold.current.fired = false; return; } onSelect(); };

  const angleAt = (e) => {
    const r = ref.current.getBoundingClientRect();
    return (Math.atan2(e.clientY - (r.top + r.height / 2), e.clientX - (r.left + r.width / 2)) * 180) / Math.PI;
  };
  const down = (e) => {
    if (e.target.closest("[data-wheel-btn]")) return;
    ref.current.setPointerCapture?.(e.pointerId);
    drag.current = { last: angleAt(e), acc: 0 };
  };
  const move = (e) => {
    if (!drag.current) return;
    const a = angleAt(e);
    let d = a - drag.current.last;
    if (d > 180) d -= 360;
    if (d < -180) d += 360;
    drag.current.last = a;
    drag.current.acc += d;
    while (Math.abs(drag.current.acc) >= STEP_DEG) {
      const dir = drag.current.acc > 0 ? 1 : -1;
      drag.current.acc -= dir * STEP_DEG;
      onStep(dir);
    }
  };
  const up = () => { drag.current = null; };

  const lbl = "absolute flex items-center justify-center text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--text-secondary)] hover:text-[var(--accent)]";
  const center = Math.round(size * 0.38);
  return (
    <div ref={ref} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
      className="wheel-ring relative flex-none cursor-grab rounded-full active:cursor-grabbing" style={{ width: size, height: size }}
      role="group" aria-label="Scroll wheel: drag around to move, center to select" data-testid="clickwheel-wheel">
      <button type="button" data-wheel-btn onClick={onMenu} className={`${lbl} left-1/2 top-2 -translate-x-1/2 px-3 py-1`} data-testid="clickwheel-menu-btn">Menu</button>
      <button type="button" data-wheel-btn onClick={() => onStep(-1)} aria-label="Previous" className={`${lbl} left-2 top-1/2 h-10 w-8 -translate-y-1/2 text-lg`} data-testid="clickwheel-prev-btn">‹</button>
      <button type="button" data-wheel-btn onClick={() => onStep(1)} aria-label="Next" className={`${lbl} right-2 top-1/2 h-10 w-8 -translate-y-1/2 text-lg`} data-testid="clickwheel-next-btn">›</button>
      <button type="button" data-wheel-btn onClick={onShuffle} aria-label="Shuffle a favorite" className={`${lbl} bottom-2 left-1/2 -translate-x-1/2 px-3 py-1`} data-testid="clickwheel-shuffle-btn"><Shuffle size={15} /></button>
      <button type="button" data-wheel-btn onClick={selectClick} onPointerDown={holdDown} onPointerUp={holdEnd} onPointerLeave={holdEnd} onPointerCancel={holdEnd} onContextMenu={(e) => e.preventDefault()} aria-label={onSelectHold ? "Select (hold to see friends)" : "Select"}
        className="wheel-center absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full transition-transform"
        style={{ width: center, height: center }} data-testid="clickwheel-select-btn">
        <span className="h-3 w-3 rounded-full bg-[var(--accent)]" />
      </button>
    </div>
  );
}
